
import { useState } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";

export const useQuoteDeleter = (onQuoteUpdate?: () => void) => {
  const [deletingQuoteId, setDeletingQuoteId] = useState<string | null>(null);
  const { user } = useAuth();

  const handleDeleteQuote = async () => {
    if (!deletingQuoteId) return;
    
    try {
      console.log('[QUOTE-DELETE] Starting deletion process for quote:', deletingQuoteId);
      console.log('[QUOTE-DELETE] Current user context:', user?.id);
      console.log('[QUOTE-DELETE] Auth session:', await supabase.auth.getSession());
      
      // Step 1: Verify quote exists before deletion
      const { data: existingQuote, error: fetchError } = await supabase
        .from('quotes')
        .select('id, booking_id, status')
        .eq('id', deletingQuoteId)
        .single();

      if (fetchError) {
        console.error('[QUOTE-DELETE] Error fetching quote before deletion:', fetchError);
        toast.error('Quote not found or access denied');
        setDeletingQuoteId(null);
        return;
      }

      if (!existingQuote) {
        console.log('[QUOTE-DELETE] Quote does not exist:', deletingQuoteId);
        toast.error('Quote not found');
        setDeletingQuoteId(null);
        return;
      }

      console.log('[QUOTE-DELETE] Quote exists, proceeding with deletion:', existingQuote);

      // Step 2: Attempt deletion with enhanced error checking
      const { data: deletionData, error: deletionError, count } = await supabase
        .from('quotes')
        .delete({ count: 'exact' })
        .eq('id', deletingQuoteId);

      console.log('[QUOTE-DELETE] Deletion response:', { 
        data: deletionData, 
        error: deletionError, 
        count,
        affectedRows: count 
      });

      if (deletionError) {
        console.error('[QUOTE-DELETE] Database error during deletion:', deletionError);
        toast.error(`Failed to delete quote: ${deletionError.message}`);
        setDeletingQuoteId(null);
        return;
      }

      // Step 3: Verify deletion was successful by checking affected rows
      if (count === 0) {
        console.error('[QUOTE-DELETE] No rows were affected - deletion failed silently');
        toast.error('Quote deletion failed - no rows affected. Check permissions.');
        setDeletingQuoteId(null);
        return;
      }

      // Step 4: Double-check by trying to fetch the deleted quote
      const { data: verificationCheck, error: verificationError } = await supabase
        .from('quotes')
        .select('id')
        .eq('id', deletingQuoteId)
        .maybeSingle();

      if (verificationError) {
        console.error('[QUOTE-DELETE] Error during verification check:', verificationError);
        // Continue anyway as deletion might have succeeded
      }

      if (verificationCheck) {
        console.error('[QUOTE-DELETE] CRITICAL: Quote still exists after deletion!', verificationCheck);
        toast.error('Quote deletion failed - record still exists in database');
        setDeletingQuoteId(null);
        return;
      }

      // Step 5: Success - quote is actually deleted
      console.log('[QUOTE-DELETE] Quote successfully deleted and verified:', {
        quoteId: deletingQuoteId,
        affectedRows: count,
        verificationPassed: !verificationCheck
      });
      
      toast.success('Quote deleted successfully');
      
      // Reset the deleting state immediately
      setDeletingQuoteId(null);
      
      // Trigger data refresh with a small delay to ensure database propagation
      setTimeout(() => {
        console.log('[QUOTE-DELETE] Triggering quote data refresh');
        if (onQuoteUpdate) {
          onQuoteUpdate();
        }
      }, 100);
      
    } catch (error) {
      console.error('[QUOTE-DELETE] Unexpected error during quote deletion:', error);
      console.error('[QUOTE-DELETE] Error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
      toast.error(`Failed to delete quote: ${error.message || 'Unknown error'}`);
      setDeletingQuoteId(null);
    }
  };

  return {
    deletingQuoteId,
    setDeletingQuoteId,
    handleDeleteQuote
  };
};
